<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center flex-wrap text-white" style="padding: 32px 0; gap: 14px;">
            <div class="header-icon-wrap"><i class="fe fe-users" style="font-size: 22px;"></i></div>
            <div class="flex-grow-1">
              <h4 class="page-title text-white">My patients</h4>
              <small style="opacity: .8;">Patients enrolled at your facility and their dependants</small>
            </div>
            <button type="button" class="btn btn-light btn-sm font-weight-bold" :disabled="!approved" @click="openAdd">
              <i class="fe fe-user-plus mr-1"></i> Add patient
            </button>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">
        <IndependentStatusBanner class="mb-4" show-link />

        <div class="card shadow-sm table-card">
          <div class="px-4 py-3 d-flex align-items-center flex-wrap" style="gap: 12px; border-bottom: 1px solid #f1f5f9;">
            <label for="mpt-q" class="sr-only">Search patients</label>
            <input id="mpt-q" v-model="q" type="search" class="form-control" style="max-width: 320px;"
              placeholder="Name, ID number or phone…" @input="debouncedLoad" />
            <span class="ml-auto small text-muted">{{ total.toLocaleString() }} patients</span>
          </div>
          <div class="table-responsive">
            <table class="table align-td-middle mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">Patient</th>
                  <th class="table-th">ID number</th>
                  <th class="table-th">Phone</th>
                  <th class="table-th">Dependants</th>
                  <th class="table-th">Enrolled</th>
                  <th class="table-th"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in rows" :key="p.id" class="table-row">
                  <td class="px-4">
                    <router-link :to="`/patient/client/${p.id}`" class="font-weight-bold">{{ p.full_name }}</router-link>
                    <small class="d-block text-muted">{{ p.gender }}<span v-if="p.dob"> · {{ age(p.dob) }} yrs</span></small>
                  </td>
                  <td><code>{{ p.id_card_number }}</code></td>
                  <td>{{ p.phone_number }}</td>
                  <td>
                    <div v-for="d in p.dependants" :key="d.id" class="small">
                      <router-link :to="`/patient/dependent/${d.id}`">{{ d.firstname }} {{ d.lastname }}</router-link>
                      <span class="text-muted"> · {{ d.relationShipType }}</span>
                      <button type="button" class="btn btn-link btn-sm p-0 ml-1" :disabled="!approved" title="Book a consultation"
                        @click="book({ type: 'Dependent', patient_id: p.id, dependent_id: d.id, name: d.firstname + ' ' + d.lastname, id_card_number: d.id_card_number, has_email: !!p.email })">
                        <i class="fe fe-video"></i>
                      </button>
                    </div>
                    <button type="button" class="btn btn-link btn-sm p-0" :disabled="!approved" @click="openDependant(p)">
                      <i class="fe fe-plus mr-1"></i>Add dependant
                    </button>
                  </td>
                  <td class="text-nowrap small">{{ (p.created_at || "").slice(0, 10) }}</td>
                  <td class="text-right pr-4 text-nowrap">
                    <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!approved" title="Book a telemedicine consultation"
                      @click="book({ type: 'client', patient_id: p.id, dependent_id: null, name: p.full_name, id_card_number: p.id_card_number, has_email: !!p.email })">
                      <i class="fe fe-video mr-1"></i> Book
                    </button>
                  </td>
                </tr>
                <tr v-if="!rows.length">
                  <td colspan="6" class="text-center text-muted py-5">
                    <i class="fe fe-inbox" style="font-size: 2rem; opacity: .4;"></i>
                    <p class="mt-2 mb-0">{{ loading ? "Loading…" : q ? "No patient matches your search." : "No patients yet." }}</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="lastPage > 1" class="d-flex justify-content-center py-3" style="gap: 8px;">
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page <= 1" @click="go(page - 1)">Previous</button>
            <span class="small align-self-center">Page {{ page }} of {{ lastPage }}</span>
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page >= lastPage" @click="go(page + 1)">Next</button>
          </div>
        </div>
      </div>

      <!-- Add patient -->
      <div v-if="addOpen" class="mp-backdrop" role="dialog" aria-modal="true" aria-labelledby="mp-add-title" @click.self="addOpen = false">
        <form class="mp-modal" @submit.prevent="savePatient">
          <h5 id="mp-add-title" class="font-weight-bold mb-3">Add patient</h5>
          <div class="form-row">
            <div class="form-group col-md-4"><label for="mp-f" class="mp-label">First name *</label><input id="mp-f" v-model="form.firstname" class="form-control" required /></div>
            <div class="form-group col-md-4"><label for="mp-m" class="mp-label">Middle name</label><input id="mp-m" v-model="form.middlename" class="form-control" /></div>
            <div class="form-group col-md-4"><label for="mp-l" class="mp-label">Last name *</label><input id="mp-l" v-model="form.lastname" class="form-control" required /></div>
            <div class="form-group col-md-4">
              <label for="mp-g" class="mp-label">Gender *</label>
              <select id="mp-g" v-model="form.gender" class="form-control" required><option>Male</option><option>Female</option></select>
            </div>
            <div class="form-group col-md-4"><label for="mp-d" class="mp-label">Date of birth *</label><input id="mp-d" v-model="form.dob" type="date" class="form-control" required :max="today" /></div>
            <div class="form-group col-md-4"><label for="mp-p" class="mp-label">Phone *</label><input v-phone id="mp-p" v-model="form.phone_number" class="form-control" required /></div>
            <div class="form-group col-md-6">
              <label for="mp-e" class="mp-label">Email <span class="text-muted small">(for consultation links and receipts)</span></label>
              <input id="mp-e" v-model="form.email" type="email" class="form-control" />
            </div>
            <div class="form-group col-md-6"><label for="mp-a" class="mp-label">Address</label><input id="mp-a" v-model="form.address1" class="form-control" /></div>
          </div>
          <div v-if="formError" class="alert alert-danger small py-2">{{ formError }}</div>
          <div class="d-flex justify-content-end" style="gap: 8px;">
            <button type="button" class="btn btn-outline-secondary" @click="addOpen = false">Cancel</button>
            <button type="submit" class="btn btn-submit" :disabled="saving">Save patient</button>
          </div>
        </form>
      </div>

      <!-- Add dependant -->
      <div v-if="depFor" class="mp-backdrop" role="dialog" aria-modal="true" aria-labelledby="mp-dep-title" @click.self="depFor = null">
        <form class="mp-modal" @submit.prevent="saveDependant">
          <h5 id="mp-dep-title" class="font-weight-bold mb-3">Add dependant of {{ depFor.full_name }}</h5>
          <div class="form-row">
            <div class="form-group col-md-6"><label for="md-f" class="mp-label">First name *</label><input id="md-f" v-model="dep.firstname" class="form-control" required minlength="3" /></div>
            <div class="form-group col-md-6"><label for="md-l" class="mp-label">Last name *</label><input id="md-l" v-model="dep.lastname" class="form-control" required minlength="3" /></div>
            <div class="form-group col-md-4">
              <label for="md-r" class="mp-label">Relationship *</label>
              <select id="md-r" v-model="dep.relationShipType" class="form-control" required>
                <option>Spouse</option><option>Child 1</option><option>Child 2</option><option>Child 3</option><option>Child 4</option><option>Extra Dependent</option>
              </select>
            </div>
            <div class="form-group col-md-4">
              <label for="md-g" class="mp-label">Gender *</label>
              <select id="md-g" v-model="dep.gender" class="form-control" required><option>Male</option><option>Female</option></select>
            </div>
            <div class="form-group col-md-4"><label for="md-d" class="mp-label">Date of birth *</label><input id="md-d" v-model="dep.dob" type="date" class="form-control" required :max="today" /></div>
          </div>
          <div v-if="formError" class="alert alert-danger small py-2">{{ formError }}</div>
          <div class="d-flex justify-content-end" style="gap: 8px;">
            <button type="button" class="btn btn-outline-secondary" @click="depFor = null">Cancel</button>
            <button type="submit" class="btn btn-submit" :disabled="saving">Save dependant</button>
          </div>
        </form>
      </div>

      <BookConsultation :open="bookOpen" :preset="bookPatient" @close="bookOpen = false" @booked="onBooked" />
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import IndependentStatusBanner from "./IndependentStatusBanner.vue";
import BookConsultation from "./BookConsultation.vue";
import { independent, isApprovedIndependent } from "@/service/independent";

const blankPatient = () => ({ firstname: "", middlename: "", lastname: "", gender: "Female", dob: "", phone_number: "", email: "", address1: "" });
const blankDependant = () => ({ firstname: "", lastname: "", relationShipType: "Spouse", gender: "Female", dob: "" });

export default {
  components: { Navbar, IndependentStatusBanner, BookConsultation },
  data() {
    return {
      approved: isApprovedIndependent(),
      today: new Date().toISOString().slice(0, 10),
      q: "",
      rows: [],
      page: 1,
      lastPage: 1,
      total: 0,
      loading: false,
      timer: null,
      addOpen: false,
      form: blankPatient(),
      depFor: null,
      dep: blankDependant(),
      saving: false,
      formError: "",
      bookOpen: false,
      bookPatient: null,
    };
  },
  created() {
    this.load();
  },
  methods: {
    age(dob) {
      const d = new Date(dob);
      return isNaN(d) ? "" : Math.floor((Date.now() - d.getTime()) / 31557600000);
    },
    debouncedLoad() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.go(1), 300);
    },
    go(page) {
      this.page = page;
      this.load();
    },
    load() {
      this.loading = true;
      this.axios
        .get("/api/v1/auth/independent/patients/list", { params: { q: this.q, page: this.page } })
        .then((res) => {
          this.rows = res.data.data || [];
          this.lastPage = res.data.last_page || 1;
          this.total = res.data.total || 0;
        })
        .catch(() => { this.rows = []; })
        .finally(() => { this.loading = false; });
    },
    openAdd() {
      this.form = blankPatient();
      this.formError = "";
      this.addOpen = true;
    },
    openDependant(p) {
      this.dep = blankDependant();
      this.formError = "";
      this.depFor = p;
    },
    errorText(err, fallback) {
      const body = (err.response && err.response.data) || {};
      if (body.errors && typeof body.errors === "object") return Object.values(body.errors).flat().join(" ");
      return body.errors || body.message || fallback;
    },
    savePatient() {
      const agency = JSON.parse(localStorage.getItem("agency") || "{}");
      this.saving = true;
      this.formError = "";
      // The backend files the patient under this facility (agency and provider = the facility).
      this.axios
        .post("/api/v1/auth/register", {
          ...this.form,
          firstname: this.form.firstname.trim().toUpperCase(),
          middlename: this.form.middlename.trim().toUpperCase(),
          lastname: this.form.lastname.trim().toUpperCase(),
          type: "client",
          sector: "Private Patient",
          state: agency.state,
          localgovt: agency.localgovt,
        })
        .then(() => {
          this.addOpen = false;
          this.$toasted.success("Patient added", { position: "top-center", duration: 2500 });
          this.go(1);
        })
        .catch((err) => { this.formError = this.errorText(err, "Could not add the patient."); })
        .finally(() => { this.saving = false; });
    },
    saveDependant() {
      const row = independent();
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      this.saving = true;
      this.formError = "";
      this.axios
        .post("/api/v1/auth/addDependant", {
          ...this.dep,
          firstname: this.dep.firstname.trim().toUpperCase(),
          lastname: this.dep.lastname.trim().toUpperCase(),
          user_id: this.depFor.id,
          agency_id: row ? row.facility_id : null,
          provider: row ? row.facility_id : null,
          enrolled_by: user.id,
        })
        .then(() => {
          this.depFor = null;
          this.$toasted.success("Dependant added", { position: "top-center", duration: 2500 });
          this.load();
        })
        .catch((err) => { this.formError = this.errorText(err, "Could not add the dependant."); })
        .finally(() => { this.saving = false; });
    },
    book(patient) {
      this.bookPatient = patient;
      this.bookOpen = true;
    },
    onBooked(data) {
      if (data && data.id) this.$router.push(`/appointment/${data.id}`);
    },
  },
};
</script>

<style scoped>
.mp-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, .55); z-index: 1050; display: flex; align-items: flex-start; justify-content: center; padding: 40px 12px; overflow-y: auto; }
.mp-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 720px; padding: 24px; box-shadow: 0 20px 50px rgba(0,0,0,.25); }
.mp-label { font-weight: 600; font-size: .85rem; color: #374151; }
</style>
